import React, { useState, useEffect, useRef } from 'react';
import { Camera, User } from 'lucide-react';
import { userAPI, authAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatPhoneNumber, unformatPhoneNumber } from '../utils/phoneFormatter';
import { extractErrorMessage } from '../utils/errorHandler';
import appConfig from '../config/appConfig';
import textConfig from '../config/textConfig';
import toast from 'react-hot-toast';
import './ProfileCompletionModal.css';
import logger from '../utils/logger';
import ImageEditor from './ImageEditor';
import { autoCorrectImageOrientation } from '../utils/imageOrientation';
import {
  getOnboardingGate,
  verificationDeferred,
  verificationRequiredNow,
} from '../utils/onboardingGate';

// 2-step modal states (phone-verification spec §2).
const STEP_FORM = 'form';
const STEP_OTP = 'otp';
const STEP_ADMIN_REVIEW = 'admin-review';

// Feature flag: profile-photo capture during registration.
// false → registration is phone + SMS consent + OTP only; the photo step is
//         hidden and NOT required. Users add a photo later from Profile Edit.
// true  → original behavior (photo required to complete registration).
// Flip back to `true` to re-enable. See docs/MAKE_PROFILE_PHOTO_OPTIONAL_PLAN.md
const PHOTO_ENABLED = false;

// Phone-verification error code → user-facing copy. Spec §3 — don't string-match
// backend messages; rely on the `error` code.
const OTP_ERROR_COPY = {
  OTP_INVALID_FORMAT: 'Use international format (+1...).',
  OTP_INVALID_NUMBER: 'Invalid phone number. Please check and try again.',
  OTP_NOT_MOBILE: 'This number cannot receive SMS. Please use a mobile number.',
  OTP_REGION_BLOCKED: 'Cannot send to this region. Please use a US mobile number.',
  OTP_PHONE_TAKEN: 'This number is already in use.',
  OTP_TWILIO_ERROR: 'Failed to send. Please try again.',
  OTP_COOLDOWN: 'Too many wrong attempts. Try again in 4 hours, or use a different number.',
};

// Pull { code, message, status } out of any axios-shaped OTP error. Backend
// puts the payload in either response.data or response.data.detail.
const extractOtpError = (error) => {
  const data = error?.response?.data;
  const detail = data?.detail;
  const detailObj = typeof detail === 'object' && detail !== null ? detail : null;
  const code = detailObj?.error || data?.error || null;
  const message =
    detailObj?.message ||
    (typeof detail === 'string' ? detail : null) ||
    data?.message ||
    'Failed to send code';
  return { code, message, status: error?.response?.status };
};

const ProfileCompletionModal = ({ isOpen, onClose, user, onSave, gateNotice = null }) => {
  const { logout } = useAuth();
  // Restore phone/OTP state from sessionStorage so a page reload keeps everything
  const savedPhone = sessionStorage.getItem('pc_otp_phone') || '';
  const savedOtpSent =
    sessionStorage.getItem('pc_otp_sent') === 'true' && savedPhone.length === 10;
  const savedExpiry = Number(sessionStorage.getItem('pc_otp_expiry') || 0);
  const savedRemaining = savedOtpSent
    ? Math.max(0, Math.floor((savedExpiry - Date.now()) / 1000))
    : 0;

  const [step, setStep] = useState(savedOtpSent ? STEP_OTP : STEP_FORM);
  const [mobileNumber, setMobileNumber] = useState(savedPhone);
  const [profileImage, setProfileImage] = useState(null);
  const [profileImagePreview, setProfileImagePreview] = useState(null);
  const [smsConsent, setSmsConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [showImageEditor, setShowImageEditor] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpSending, setOtpSending] = useState(false);
  const [otpVerifying, setOtpVerifying] = useState(false);
  const [otpError, setOtpError] = useState('');
  const [otpBanner, setOtpBanner] = useState(''); // for COOLDOWN / SEND_LIMIT
  // Sticky "3 wrong codes" cooldown state (spec §2.7 Scenario A). Locks the
  // OTP cells, Verify, and Resend for the 4-hour backend cooldown. The user
  // is NOT escalated to admin — they can wait, use a different number, or
  // sign out. Cleared by handleUseDifferentNumber.
  const [verifyCooldown, setVerifyCooldown] = useState(false);
  // Seconds until the current code expires (backend OTP_EXPIRY_MINUTES = 5).
  // Restored from sessionStorage so a reload keeps the live countdown.
  const [otpExpiry, setOtpExpiry] = useState(savedRemaining);
  // Last OTP error we hand back to /auth/register or /auth/complete-profile
  // when we have to bail out via §2.4 auto-submit (spec §11).
  const lastOtpErrorRef = useRef(null);
  const expiryTimerRef = useRef(null);

  const needsPhone = !user?.phone_number || user.phone_number.length === 0;

  // Reset all OTP-related state and storage. Called when the user clicks
  // "Use a different number" or after a successful submit.
  const clearOtpState = () => {
    sessionStorage.removeItem('pc_otp_phone');
    sessionStorage.removeItem('pc_otp_sent');
    sessionStorage.removeItem('pc_otp_expiry');
    sessionStorage.removeItem('pc_phone_verified');
    setOtpCode('');
    setOtpError('');
    setOtpBanner('');
    setVerifyCooldown(false);
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    setOtpExpiry(0);
    lastOtpErrorRef.current = null;
  };

  // Initialize mobile number / image preview when user changes
  useEffect(() => {
    if (!user) {
      const hasSavedPhone = (sessionStorage.getItem('pc_otp_phone') || '').length === 10;
      if (!hasSavedPhone) setMobileNumber('');
      setProfileImage(null);
      setProfileImagePreview(null);
      setErrors({});
      return;
    }

    setProfileImage(null);
    setProfileImagePreview(null);
    setSmsConsent(false);
    setErrors({});

    const hasSavedOtp = (sessionStorage.getItem('pc_otp_phone') || '').length === 10 &&
      sessionStorage.getItem('pc_otp_sent') === 'true';

    if (!hasSavedOtp) {
      setMobileNumber('');
      setStep(STEP_FORM);
      clearOtpState();

      if (user?.phone_number) {
        const cleaned = user.phone_number.replace(/\D/g, '');
        if (cleaned.length === 11 && cleaned.startsWith('1')) {
          setMobileNumber(cleaned.substring(1));
        } else if (cleaned.length === 10) {
          setMobileNumber(cleaned);
        }
      }
    }

    if (user?.user_image) {
      setProfileImagePreview(user.user_image);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Block ESC from closing the modal while the form is incomplete
  useEffect(() => {
    if (!isOpen) return;
    const handleEscKey = (e) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        handleCloseAttempt();
      }
    };
    document.addEventListener('keydown', handleEscKey, true);
    return () => document.removeEventListener('keydown', handleEscKey, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, user, mobileNumber, profileImage, smsConsent, step]);

  // Resume code-expiry countdown on mount if any time remains
  useEffect(() => {
    if (savedRemaining > 0) startExpiryCountdown(savedRemaining);
    return () => {
      if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Live "code expires in M:SS" countdown for the active OTP.
  const startExpiryCountdown = (seconds) => {
    if (expiryTimerRef.current) clearInterval(expiryTimerRef.current);
    setOtpExpiry(seconds);
    expiryTimerRef.current = setInterval(() => {
      setOtpExpiry(prev => {
        if (prev <= 1) {
          clearInterval(expiryTimerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const formatTimer = (totalSeconds) => {
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!appConfig.upload.allowedImageTypes.includes(file.type)) {
      toast.error('Please upload a valid image file (JPEG, PNG, GIF, WebP, or BMP)');
      e.target.value = '';
      return;
    }
    const maxSize = appConfig.getMaxImageSizeBytes();
    if (file.size > maxSize) {
      toast.error(`Image size should not exceed ${appConfig.getMaxImageSizeText()}`);
      e.target.value = '';
      return;
    }
    try {
      const { file: correctedFile, dataUrl } = await autoCorrectImageOrientation(file);
      setProfileImage(correctedFile);
      setProfileImagePreview(dataUrl);
      e.target.value = '';
    } catch (error) {
      logger.error('PROFILECOMPLETIONMODAL', 'Image orientation correction failed:', error);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImage(file);
        setProfileImagePreview(reader.result);
        e.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const handleImageEditorSave = (file, dataUrl) => {
    setProfileImage(file);
    setProfileImagePreview(dataUrl);
    setShowImageEditor(false);
  };

  const handleImageEditorCancel = () => {
    if (!profileImage) {
      setProfileImagePreview(user?.user_image || null);
    }
    setShowImageEditor(false);
  };

  const handleEditExistingImage = () => {
    if (!profileImagePreview) return;
    setShowImageEditor(true);
  };

  // Step 1 validation — image + phone (if needed) + consent
  const validateStepForm = () => {
    const newErrors = {};
    const hasExistingImage = user?.user_image && user.user_image.length > 0;
    const hasNewImage = profileImage !== null;

    if (needsPhone) {
      if (!mobileNumber || mobileNumber.length < 10) {
        newErrors.phone = 'Please enter a valid 10-digit phone number';
      }
    }
    if (PHOTO_ENABLED && !hasExistingImage && !hasNewImage) {
      newErrors.image = 'Profile photo is required to continue';
    }
    if (!smsConsent) {
      newErrors.smsConsent = 'You must agree to receive SMS messages';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Persist a verified-phone submission, OR a §2.4 auto-submit. `unverifiedReason`
  // is the last OTP error string forwarded as `last_otp_error` (spec §11) so
  // admin can see why Twilio failed.
  const submitProfile = async ({ unverifiedReason = null } = {}) => {
    setLoading(true);
    try {
      const hasExistingPhone = user?.phone_number && user.phone_number.length > 0;
      const phoneToSend = hasExistingPhone
        ? user.phone_number
        : (mobileNumber ? `+1${mobileNumber}` : '');

      await userAPI.completeProfile(
        phoneToSend,
        smsConsent,
        profileImage,
        { lastOtpError: unverifiedReason }
      );

      clearOtpState();

      if (unverifiedReason) {
        // Show the in-modal "admin will review" screen — onSave/onClose happen
        // when the user clicks the sign-out link or navigates away.
        setStep(STEP_ADMIN_REVIEW);
        return;
      }

      toast.success('Profile completed! Your account is pending admin approval.');
      if (onSave) await onSave();
      onClose();
    } catch (error) {
      logger.error('PROFILECOMPLETIONMODAL', 'Profile completion error:', { error });
      const msg = extractErrorMessage(error, 'Failed to complete profile. Please try again or contact support.');
      toast.error(msg, { duration: 6000 });
    } finally {
      setLoading(false);
    }
  };

  // ----- Step 1: Continue -----
  // If the user already has a verified phone in their account, skip OTP and
  // submit directly. Otherwise kick off send-otp and transition to step 2.
  const handleContinue = async () => {
    if (!validateStepForm()) return;

    if (!needsPhone) {
      await submitProfile();
      return;
    }

    setOtpSending(true);
    setOtpError('');
    setOtpBanner('');
    try {
      const fullPhone = `+1${mobileNumber}`;
      const response = await authAPI.sendOTP(fullPhone);
      if (response?.success) {
        sessionStorage.setItem('pc_otp_phone', mobileNumber);
        sessionStorage.setItem('pc_otp_sent', 'true');
        const expirySeconds = (response.expires_in_minutes || 5) * 60;
        sessionStorage.setItem('pc_otp_expiry', String(Date.now() + expirySeconds * 1000));
        setStep(STEP_OTP);
        setOtpCode('');
        startExpiryCountdown(expirySeconds);
        toast.success('Verification code sent!');
      }
    } catch (error) {
      const { code, message, status } = extractOtpError(error);
      lastOtpErrorRef.current = code ? `${code}: ${message}` : message;

      // 503 SEND_LIMIT — admin escalation. Auto-submit without OTP (spec §2.4).
      if (status === 503 || code === 'OTP_SEND_LIMIT') {
        await submitProfile({ unverifiedReason: lastOtpErrorRef.current });
        return;
      }
      // 429 COOLDOWN — banner, stay on step 1.
      if (status === 429 || code === 'OTP_COOLDOWN') {
        setOtpBanner(OTP_ERROR_COPY.OTP_COOLDOWN);
        return;
      }
      // 400 family — inline error under phone field.
      const copy = (code && OTP_ERROR_COPY[code]) || message;
      setErrors(prev => ({ ...prev, phone: copy }));
    } finally {
      setOtpSending(false);
    }
  };

  // ----- Step 2: Verify & Finish -----
  const handleVerifyAndFinish = async () => {
    if (!otpCode || otpCode.length !== 6) {
      setOtpError('Please enter the 6-digit code.');
      return;
    }
    setOtpVerifying(true);
    setOtpError('');
    try {
      const fullPhone = `+1${mobileNumber}`;
      const response = await authAPI.verifyOTP(fullPhone, otpCode);

      if (response?.verified) {
        sessionStorage.setItem('pc_phone_verified', 'true');
        sessionStorage.removeItem('pc_otp_sent');
        sessionStorage.removeItem('pc_otp_expiry');
        await submitProfile();
        return;
      }

      // Wrong code. Spec §2.7 Scenario A: 3rd wrong code → 4-hour cooldown,
      // NOT admin escalation. The user typed wrong digits — they can wait or
      // switch to a different number themselves.
      const remaining = response?.attempts_remaining;
      if (remaining === 0) {
        setVerifyCooldown(true);
        setOtpError('Too many wrong attempts. Try again in 4 hours, or use a different number.');
        setOtpCode('');
        return;
      }
      if (remaining === null || remaining === undefined) {
        setOtpError('Code expired — request a new one.');
      } else {
        setOtpError(response?.message || `Invalid code. ${remaining} attempt(s) remaining.`);
      }
      setOtpCode('');
    } catch (error) {
      const { message } = extractOtpError(error);
      setOtpError(message || 'Verification failed');
    } finally {
      setOtpVerifying(false);
    }
  };

  // ----- Step 2: Resend -----
  const handleResend = async () => {
    if (otpSending) return;
    setOtpSending(true);
    setOtpError('');
    try {
      const fullPhone = `+1${mobileNumber}`;
      const response = await authAPI.sendOTP(fullPhone);
      if (response?.success) {
        const expirySeconds = (response.expires_in_minutes || 5) * 60;
        sessionStorage.setItem('pc_otp_expiry', String(Date.now() + expirySeconds * 1000));
        startExpiryCountdown(expirySeconds);
        toast.success('Code resent');
        setOtpCode('');
      }
    } catch (error) {
      const { code, message, status } = extractOtpError(error);
      lastOtpErrorRef.current = code ? `${code}: ${message}` : message;
      // 503 SEND_LIMIT on resend triggers auto-submit, same as Continue.
      if (status === 503 || code === 'OTP_SEND_LIMIT') {
        await submitProfile({ unverifiedReason: lastOtpErrorRef.current });
        return;
      }
      if (status === 429 || code === 'OTP_COOLDOWN') {
        // Defensive: if the user somehow triggers Resend during verify cooldown,
        // surface the same 4-hour message and keep everything disabled.
        setVerifyCooldown(true);
        setOtpError(OTP_ERROR_COPY.OTP_COOLDOWN);
        return;
      }
      setOtpError((code && OTP_ERROR_COPY[code]) || message);
    } finally {
      setOtpSending(false);
    }
  };

  // ----- Step 2: Use a different number → back to step 1, form preserved -----
  const handleUseDifferentNumber = () => {
    clearOtpState();
    setStep(STEP_FORM);
  };

  // ----- Sign out -----
  // Used by both the admin-review screen (spec §2.4) and the Scenario A
  // 4-hour cooldown (spec §2.7) — the user shouldn't be trapped in the modal.
  const handleSignOut = async () => {
    onClose();
    try {
      await logout();
    } catch (err) {
      logger.error('PROFILECOMPLETIONMODAL', 'Sign out failed', { err });
    }
  };

  const handleCloseAttempt = () => {
    // Admin-review screen: closing is OK, the data is already saved.
    if (step === STEP_ADMIN_REVIEW) {
      onClose();
      return;
    }
    const hasExistingImage = user?.user_image && user.user_image.length > 0;
    const hasNewImage = profileImage !== null;
    const missingPhone = needsPhone && (!mobileNumber || mobileNumber.length < 10);
    const missingImage = PHOTO_ENABLED && !hasExistingImage && !hasNewImage;
    if (!missingPhone && !missingImage && smsConsent && step === STEP_FORM) {
      onClose();
      return;
    }
    const missing = [];
    if (missingPhone) missing.push('phone number');
    if (missingImage) missing.push('profile photo');
    if (!smsConsent) missing.push('SMS consent');
    if (missing.length > 0) {
      // Stable id: repeated outside-clicks update the same toast instead of stacking
      toast.error(`Please complete: ${missing.join(', ')}`, { id: 'pc-modal-incomplete', duration: 4000, icon: '⚠️' });
    }
  };

  if (!isOpen) return null;

  // ===== Admin-review terminal screen (spec §2.4) =====
  if (step === STEP_ADMIN_REVIEW) {
    return (
      <div className="modal-overlay">
        <div className="modal-content pc-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header pc-modal-header">
            <h3>Your profile has been saved</h3>
          </div>
          <div className="modal-body pc-modal-body">
            <p style={{ fontSize: '15px', lineHeight: 1.5, marginBottom: '12px' }}>
              We had trouble verifying your phone number. An admin will review your
              account and reach out shortly. You'll have access once approved.
            </p>
          </div>
          <div className="modal-footer pc-modal-footer">
            <button type="button" onClick={handleSignOut} className="pc-complete-btn">
              Sign out
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Step 2: OTP entry =====
  if (step === STEP_OTP) {
    const displayPhone = formatPhoneNumber(mobileNumber);
    return (
      <div className="modal-overlay" onClick={handleCloseAttempt}>
        <div className="modal-content pc-modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-header pc-modal-header">
            <h3>Verify Your Number</h3>
          </div>
          <div className="modal-body pc-modal-body">
            <p style={{ fontSize: '15px', marginBottom: '16px' }}>
              We sent a 6-digit code to {displayPhone}.
            </p>

            {!otpBanner && !verifyCooldown && (
              <p
                style={{
                  fontSize: '13px',
                  marginTop: '-8px',
                  marginBottom: '16px',
                  color: otpExpiry > 0 ? '#6b7280' : '#dc2626',
                }}
              >
                {otpExpiry > 0
                  ? `Code expires in ${formatTimer(otpExpiry)}`
                  : 'Code expired — request a new one.'}
              </p>
            )}

            {otpBanner && (
              <div
                role="alert"
                style={{
                  padding: '10px 12px',
                  background: '#fef3c7',
                  border: '1px solid #fde68a',
                  borderRadius: '6px',
                  marginBottom: '12px',
                  fontSize: '13px',
                  color: '#92400e',
                }}
              >
                {otpBanner}
              </div>
            )}

            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={otpCode}
              onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, ''))}
              placeholder="------"
              aria-label="6-digit verification code"
              className="pc-phone-field"
              disabled={verifyCooldown}
              style={{
                width: '200px',
                letterSpacing: '10px',
                textAlign: 'center',
                fontSize: '20px',
                fontWeight: 600,
                padding: '12px',
                border: '2px solid #d1d5db',
                borderRadius: '8px',
                opacity: verifyCooldown ? 0.6 : 1,
              }}
              autoFocus={!verifyCooldown}
            />

            {otpError && <div className="pc-error-text" style={{ marginTop: '8px' }}>{otpError}</div>}

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <button
                type="button"
                onClick={handleResend}
                disabled={otpSending || !!otpBanner || verifyCooldown}
                style={{
                  background: 'none',
                  border: 'none',
                  color: (!!otpBanner || verifyCooldown) ? '#9ca3af' : '#3b82f6',
                  cursor: (!!otpBanner || verifyCooldown) ? 'not-allowed' : 'pointer',
                  padding: 0,
                  fontSize: '13px',
                  textAlign: 'left',
                }}
              >
                {otpSending
                  ? 'Sending...'
                  : verifyCooldown
                    ? 'Resend disabled — too many wrong codes'
                    : "Didn't get it? Resend code"}
              </button>
              <button
                type="button"
                onClick={handleUseDifferentNumber}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#3b82f6',
                  cursor: 'pointer',
                  padding: 0,
                  fontSize: '13px',
                  textAlign: 'left',
                }}
              >
                Use a different number
              </button>
              {verifyCooldown && (
                <button
                  type="button"
                  onClick={handleSignOut}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    padding: 0,
                    fontSize: '13px',
                    textAlign: 'left',
                  }}
                >
                  Sign out
                </button>
              )}
            </div>
          </div>
          <div className="modal-footer pc-modal-footer">
            <button
              type="button"
              onClick={handleVerifyAndFinish}
              className="pc-complete-btn"
              disabled={loading || otpVerifying || otpCode.length < 6 || verifyCooldown}
            >
              {loading || otpVerifying ? 'Verifying...' : 'Verify & Finish'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ===== Step 1: Form (image + phone + consent) =====
  const hasExistingImage = user?.user_image && user.user_image.length > 0;
  const hasNewImage = profileImage !== null;
  const continueDisabled =
    loading || otpSending || showImageEditor ||
    (needsPhone && mobileNumber.length < 10) ||
    (PHOTO_ENABLED && !hasExistingImage && !hasNewImage) ||
    !smsConsent;

  return (
    <div className="modal-overlay" onClick={handleCloseAttempt}>
      <div className="modal-content pc-modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Onboarding gate (A/B): security notice ABOVE the title so the
            reason for the modal is the first thing the user reads */}
        {gateNotice && (
          <div
            role="alert"
            style={{
              padding: '10px 12px',
              background: '#eff6ff',
              border: '1px solid #bfdbfe',
              borderRadius: '6px',
              margin: '20px 32px 0',
              fontSize: '13px',
              color: '#1e40af',
            }}
          >
            {gateNotice}
          </div>
        )}

        <div className="modal-header pc-modal-header">
          <h3>Setup Profile</h3>
        </div>

        <div className="modal-body pc-modal-body">
          {/* Profile image — hidden while PHOTO_ENABLED is false (photo optional, added later from Profile Edit) */}
          {PHOTO_ENABLED && (
          <div className="pc-profile-photo-section">
            {showImageEditor ? (
              <ImageEditor
                imageUrl={profileImagePreview}
                onSave={handleImageEditorSave}
                onCancel={handleImageEditorCancel}
                size={120}
              />
            ) : (
              <>
                <input
                  type="file"
                  id="profilePhotoInput"
                  accept="image/*"
                  onChange={handleImageChange}
                  className="pc-hidden-input"
                  aria-label="Upload profile photo"
                />
                <label htmlFor="profilePhotoInput" className="pc-photo-circle-label">
                  <div className="pc-photo-circle">
                    {profileImagePreview ? (
                      <>
                        <img src={profileImagePreview} alt="Profile preview" className="pc-photo-preview" />
                        <div className="pc-photo-edit-btn"><Camera size={18} /></div>
                      </>
                    ) : (
                      <div className="pc-photo-placeholder">
                        <User size={48} className="pc-photo-icon" />
                        <div className="pc-photo-camera-badge"><Camera size={18} /></div>
                      </div>
                    )}
                  </div>
                </label>
                <label htmlFor="profilePhotoInput" className="pc-upload-photo-link">
                  <span className="pc-upload-text">Upload Photo</span> <span className="pc-required-star">*</span>
                </label>
                {profileImagePreview && (
                  <button type="button" onClick={handleEditExistingImage} className="pc-edit-image-btn">
                    Edit Image
                  </button>
                )}
                {errors.image && <div className="pc-error-text">{errors.image}</div>}
              </>
            )}
          </div>
          )}

          {/* User info */}
          <div className="pc-user-info">
            <p className="pc-user-name">
              Logged in as {user?.first_name} {user?.last_name}
              {/* Email hidden for now — restore to show: ({user?.email}) */}
            </p>
          </div>

          {/* Phone (only if user doesn't already have one) */}
          {needsPhone && (
            <div className="pc-input-section">
              <label htmlFor="phone_number" className="pc-input-label">
                Mobile Number <span className="pc-required-star">*</span>
              </label>
              <div className="pc-phone-input-wrapper">
                <div className="pc-country-dropdown">
                  <span className="pc-country-flag">+1</span>
                  <span className="pc-dropdown-arrow">▼</span>
                </div>
                <input
                  id="phone_number"
                  type="tel"
                  className={`pc-phone-field ${errors.phone ? 'pc-error-border' : ''}`}
                  value={formatPhoneNumber(mobileNumber)}
                  onChange={(e) => {
                    const raw = unformatPhoneNumber(e.target.value).slice(0, 10);
                    setMobileNumber(raw);
                    if (errors.phone) setErrors({ ...errors, phone: null });
                  }}
                  placeholder="(XXX) XXX - XXXX"
                />
              </div>
              {errors.phone && <div className="pc-error-text">{errors.phone}</div>}
              {otpBanner && (
                <div
                  role="alert"
                  style={{
                    padding: '10px 12px',
                    background: '#fef3c7',
                    border: '1px solid #fde68a',
                    borderRadius: '6px',
                    marginTop: '8px',
                    fontSize: '13px',
                    color: '#92400e',
                  }}
                >
                  {otpBanner}
                </div>
              )}
            </div>
          )}

          {/* SMS consent */}
          <div className="pc-consent-section">
            <div className="pc-checkbox-wrapper">
              <input
                type="checkbox"
                id="sms_consent"
                checked={smsConsent}
                onChange={(e) => {
                  setSmsConsent(e.target.checked);
                  if (errors.smsConsent) setErrors({ ...errors, smsConsent: null });
                }}
                className={`pc-checkbox ${errors.smsConsent ? 'pc-error-border' : ''}`}
                aria-required="true"
                aria-invalid={errors.smsConsent ? 'true' : 'false'}
              />
              <label htmlFor="sms_consent" className="pc-checkbox-label">
                I agree to receive text messages from {textConfig.companyName} regarding concierge alerts, workspace updates, and community event notifications. Message and data rates may apply. Message frequency varies. Text HELP for help, STOP to opt-out. View our{' '}
                <a href="https://kiln.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="pc-consent-link">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://kiln.com/terms-conditions/" target="_blank" rel="noopener noreferrer" className="pc-consent-link">Terms & Conditions</a>. <span className="pc-required-star">*</span>
              </label>
            </div>
            {errors.smsConsent && <div className="pc-error-text" role="alert">{errors.smsConsent}</div>}
          </div>
        </div>

        <div className="modal-footer pc-modal-footer">
          <button
            type="button"
            onClick={handleContinue}
            className="pc-complete-btn"
            disabled={continueDisabled}
            title={showImageEditor ? 'Please apply or cancel image changes first' : ''}
          >
            {loading || otpSending
              ? 'Please wait...'
              : !needsPhone
                ? 'Complete Setup'
                : 'Continue'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Spec §4 — bypass_required hides the modal even when profile data is missing.
// Falls back to deriving bypass from user.status === 'approved' on hard refresh
// (when /auth/profile may not include profile_status).
ProfileCompletionModal.shouldShowModal = function(user, profileStatus = null) {
  if (!user?.id) return false;

  const bypassRequired =
    profileStatus?.bypass_required === true ||
    user?.status === 'approved';
  if (bypassRequired) return false;

  // Onboarding gate (A/B): in deferred mode (free_message_limit > 0) the modal
  // stays hidden until the backend says the free window is used up. Chat.js
  // owns the in-chat gate UX; this keeps the login-time modal quiet.
  const gate = getOnboardingGate(user, profileStatus);
  if (verificationDeferred(gate) && !verificationRequiredNow(gate)) return false;

  const hasPhone = user?.phone_number && user.phone_number.length > 0;
  const hasImage = user?.user_image && user.user_image.length > 0;
  const hasSmsConsent = user?.has_sms_consent === true;
  // Photo no longer gates the modal while PHOTO_ENABLED is false — otherwise the
  // modal would re-open forever for users who never upload a photo.
  return !hasPhone || (PHOTO_ENABLED && !hasImage) || !hasSmsConsent;
};

ProfileCompletionModal.markAsShown = function() {
  // No-op: kept for backward compatibility with App.js
};

export default ProfileCompletionModal;
